import React from 'react';
import { IBlock, IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { merge } from 'lodash';
import { BasicBlock } from '@core/components/BasicBlock';
import { t } from '@core/utils';
import { get } from 'lodash';
import { normalizeMergeTagKey } from '@core/utils/mergeTagDictionary';

export type ISpacer = IBlockData<
	{
		'container-background-color'?: string;
		height?: string;
		padding?: string;
	},
	{
		dynamicBackgroundColorTag?: string;
	}
>;

function resolveColorTagValue(
	tagExpression: string | undefined,
	dataSource?: Record<string, any>,
) {
	const rawTag = String(tagExpression || '').trim();
	if (!rawTag || !dataSource) return null;

	const extractedKey = rawTag.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() || rawTag;
	const normalizedKey = normalizeMergeTagKey(extractedKey);

	const value = get(dataSource, extractedKey) ?? get(dataSource, normalizedKey);
	if (typeof value === 'string' && value.trim().length > 0) {
		return value.trim();
	}

	return null;
}

export const Spacer: IBlock<ISpacer> = createBlock({
	get name() {
		return t('Spacer');
	},
	type: BasicType.SPACER,
	create: payload => {
		const defaultData: ISpacer = {
			type: BasicType.SPACER,
			data: {
				value: {},
			},
			attributes: {
				height: '20px',
				padding: '0px 0px 0px 0px',
				'container-background-color': '#FFFFFF',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	validParentType: [BasicType.COLUMN, BasicType.HERO],
	render(params) {
		const isPreviewMode = Boolean(params.dataSource?.previewMode);
		const dynamicBackgroundColorTag = String(
			params.data.data.value.dynamicBackgroundColorTag || '',
		).trim();
		const currentBackgroundColor = String(
			params.data.attributes['container-background-color'] || '',
		).trim();
		const isMergeTagBackground = /^\{\{\s*[^{}]+\s*\}\}$/.test(currentBackgroundColor);
		const isMissingBackgroundColor =
			!currentBackgroundColor ||
			currentBackgroundColor.toLowerCase() === 'transparent' ||
			isMergeTagBackground;
		const resolvedBackgroundColor =
			isPreviewMode && dynamicBackgroundColorTag
				? resolveColorTagValue(dynamicBackgroundColorTag, params.dataSource) ||
					dynamicBackgroundColorTag
				: '';
		const editModePlaceholderColor =
			!isPreviewMode && dynamicBackgroundColorTag && isMissingBackgroundColor
				? resolveColorTagValue(dynamicBackgroundColorTag, params.dataSource) || ''
				: '';

		if (resolvedBackgroundColor || editModePlaceholderColor) {
			return (
				<BasicBlock
					params={{
						...params,
						data: {
							...params.data,
							attributes: {
								...params.data.attributes,
								'container-background-color':
									resolvedBackgroundColor || editModePlaceholderColor,
							},
						},
					}}
					tag='mj-spacer'
				/>
			);
		}

		return (
			<BasicBlock
				params={params}
				tag='mj-spacer'
			/>
		);
	},
});
